/**
 * Configuração de providers de assinatura digital.
 *
 * RELEASE: v3.0.0 - 2026-05-03
 *  - Refatorado para o padrão visual SaaS premium das demais abas de
 *    Configurações: monocromático, sem gradientes coloridos por provider,
 *    sem rings de hover saturados. Mantém o logo real do provider como
 *    identidade legítima de marca.
 */

import { useState, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import {
  Loader2,
  FileSignature,
  Star,
  Pencil,
  Plus,
  Zap,
  ToggleLeft,
  ToggleRight,
} from 'lucide-react';
import {
  useTenantSignatureConfigs,
  type SignatureProvider,
  type TenantSignatureConfig,
} from '@/hooks/useTenantSignatureConfigs';
import { SignatureProviderConfigDialog } from './SignatureProviderConfigDialog';
import {
  SettingsCard,
  SettingsSectionHeader,
  StatusDot,
} from '@/components/tenant/SettingsInfoRow';
import { Badge } from '@/components/ui/badge';
import autentiqueLogo from '@/assets/autentique-logo.png';

// =============================================================================
// METADADOS DE LOGO POR PROVIDER (apenas a marca — sem cores no card)
// =============================================================================

/** Logo neutro de cada provider, exibido em pequeno quadrado */
const PROVIDER_LOGOS: Record<string, ReactNode> = {
  autentique: (
    <div className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-md border border-border/60 bg-card">
      <img src={autentiqueLogo} alt="Autentique" className="h-7 w-7 object-contain" />
    </div>
  ),
  docusign: (
    <div className="flex h-9 w-9 items-center justify-center rounded-md border border-border/60 bg-card">
      <span className="text-sm font-bold text-foreground">D</span>
    </div>
  ),
  zapsign: (
    <div className="flex h-9 w-9 items-center justify-center rounded-md border border-border/60 bg-card">
      <Zap className="h-4 w-4 text-foreground" />
    </div>
  ),
};

/** Logo padrão para providers não mapeados */
const DEFAULT_LOGO: ReactNode = (
  <div className="flex h-9 w-9 items-center justify-center rounded-md border border-border/60 bg-card">
    <FileSignature className="h-4 w-4 text-muted-foreground" />
  </div>
);

// =============================================================================
// LINHA DE PROVIDER
// =============================================================================

interface ProviderRowProps {
  provider: SignatureProvider;
  config: TenantSignatureConfig | undefined;
  onConfigure: () => void;
  onToggleActive: () => void;
  onSetDefault: () => void;
  isTogglingActive: boolean;
  isSettingDefault: boolean;
}

/**
 * Linha densa para um provider (Autentique, DocuSign, ZapSign).
 * Logo + nome + status (bullet) à esquerda; ações à direita.
 */
function ProviderRow({
  provider,
  config,
  onConfigure,
  onToggleActive,
  onSetDefault,
  isTogglingActive,
  isSettingDefault,
}: ProviderRowProps) {
  const isConfigured = !!config;
  const isActive = config?.is_active ?? false;
  const isDefault = config?.is_default ?? false;
  const logo = PROVIDER_LOGOS[provider.slug] ?? DEFAULT_LOGO;

  // Tom do bullet de status
  const statusTone: 'emerald' | 'muted' = isConfigured && isActive ? 'emerald' : 'muted';
  const statusLabel = !isConfigured
    ? 'Não configurado'
    : isActive
      ? 'Ativo'
      : 'Inativo';

  return (
    <div className="flex items-center justify-between gap-4 px-6 py-4 transition-colors hover:bg-muted/30">
      {/* Lado esquerdo: logo + nome + status */}
      <div className="flex min-w-0 flex-1 items-center gap-3">
        {logo}
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="truncate text-sm font-medium text-foreground">
              {provider.display_name}
            </h3>
            {isDefault && (
              <Badge
                variant="outline"
                className="h-5 gap-1 px-1.5 text-[10px] font-medium uppercase tracking-wider"
              >
                <Star className="h-2.5 w-2.5 fill-current" />
                Padrão
              </Badge>
            )}
          </div>
          <div className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
            <StatusDot tone={statusTone} />
            <span>{statusLabel}</span>
          </div>
        </div>
      </div>

      {/* Lado direito: ações compactas (padrão da aba Credenciais) */}
      <div className="flex shrink-0 items-center gap-0.5">
        {isConfigured ? (
          <>
            {/* Toggle ativo/inativo via ícone */}
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={onToggleActive}
              disabled={isTogglingActive}
              title={isActive ? 'Desativar' : 'Ativar'}
            >
              {isActive ? (
                <ToggleRight className="h-4 w-4 text-emerald-600" />
              ) : (
                <ToggleLeft className="h-4 w-4 text-muted-foreground" />
              )}
            </Button>
            {/* Definir como padrão */}
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={onSetDefault}
              disabled={isDefault || isSettingDefault}
              title={isDefault ? 'Provider padrão' : 'Definir como padrão'}
            >
              <Star
                className={`h-4 w-4 ${
                  isDefault ? 'fill-current text-foreground' : 'text-muted-foreground'
                }`}
              />
            </Button>
            {/* Editar configuração */}
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={onConfigure}
              title="Editar configuração"
            >
              <Pencil className="h-4 w-4" />
            </Button>
          </>
        ) : (
          // Quando ainda não configurado: CTA primário com texto
          <Button
            variant="default"
            size="sm"
            onClick={onConfigure}
            className="h-8 gap-1.5"
          >
            <Plus className="h-3.5 w-3.5" />
            Configurar
          </Button>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// COMPONENTE PRINCIPAL
// =============================================================================

/**
 * Painel de configuração de providers de assinatura digital.
 * Card único com header padrão e lista densa de providers.
 */
export function SignatureProvidersSettings() {
  const { providers, configs, isLoading, toggleActive, setDefault } =
    useTenantSignatureConfigs();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<SignatureProvider | null>(null);
  const [selectedConfig, setSelectedConfig] = useState<TenantSignatureConfig | null>(null);

  const configuredCount = configs.length;

  /** Abre o dialog de configuração/edição */
  const handleConfigure = (provider: SignatureProvider) => {
    const existingConfig = configs.find((c) => c.provider_id === provider.id);
    setSelectedProvider(provider);
    setSelectedConfig(existingConfig ?? null);
    setDialogOpen(true);
  };

  /** Alterna ativo/inativo */
  const handleToggleActive = (config: TenantSignatureConfig) => {
    toggleActive.mutate({ configId: config.id, isActive: !config.is_active });
  };

  /** Marca como padrão */
  const handleSetDefault = (config: TenantSignatureConfig) => {
    if (!config.is_default) setDefault.mutate(config.id);
  };

  return (
    <>
      <SettingsCard>
        <SettingsSectionHeader
          eyebrow="Assinatura Digital"
          title="Providers"
          description={
            providers.length === 0
              ? 'Nenhum provider disponível.'
              : configuredCount > 0
                ? `${configuredCount} de ${providers.length} provider${providers.length !== 1 ? 's' : ''} configurado${configuredCount !== 1 ? 's' : ''}.`
                : `${providers.length} provider${providers.length !== 1 ? 's' : ''} disponível — nenhum configurado ainda.`
          }
        />

        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : providers.length === 0 ? (
          <div className="flex flex-col items-center justify-center px-6 py-14 text-center">
            <FileSignature
              className="mb-3 h-8 w-8 text-muted-foreground/60"
              strokeWidth={1.5}
            />
            <p className="text-sm font-medium text-foreground">
              Nenhum provider disponível
            </p>
            <p className="mt-1 max-w-sm text-xs text-muted-foreground">
              Entre em contato com o suporte para habilitar providers de assinatura
              digital.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-border/40">
            {providers.map((provider) => {
              const config = configs.find((c) => c.provider_id === provider.id);
              return (
                <li key={provider.id}>
                  <ProviderRow
                    provider={provider}
                    config={config}
                    onConfigure={() => handleConfigure(provider)}
                    onToggleActive={() => config && handleToggleActive(config)}
                    onSetDefault={() => config && handleSetDefault(config)}
                    isTogglingActive={toggleActive.isPending}
                    isSettingDefault={setDefault.isPending}
                  />
                </li>
              );
            })}
          </ul>
        )}
      </SettingsCard>

      {/* Dialog de configuração */}
      <SignatureProviderConfigDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        provider={selectedProvider}
        existingConfig={selectedConfig}
      />
    </>
  );
}
